import { cn } from "@/lib/utils";
import type { CrPriority } from "@shared/types/supabase.types";

// Linear-style priority glyphs (CR-44), hand-rolled like the task-status
// icons in docs-tiptap's task-item-view: 16×16 viewBox, currentColor, so
// they inherit text color and scale with `size-*` utilities.

/** Signal bars, bottom-aligned at y=14: short (low), medium, tall (high). */
const BARS = [
  { x: 1.5, y: 8, height: 6 },
  { x: 6.5, y: 5, height: 9 },
  { x: 11.5, y: 2, height: 12 },
];

const FILLED_BARS: Record<"low" | "medium" | "high", number> = {
  low: 1,
  medium: 2,
  high: 3,
};

interface CrPriorityIconProps {
  priority: CrPriority;
  className?: string;
}

export function CrPriorityIcon({ priority, className }: CrPriorityIconProps) {
  if (priority === "urgent") {
    // Filled orange square with an exclamation mark.
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        className={cn("shrink-0 text-orange-500", className)}
      >
        <rect x="1.5" y="1.5" width="13" height="13" rx="3.5" fill="currentColor" />
        <rect x="7.25" y="4" width="1.5" height="5" rx="0.75" fill="white" />
        <circle cx="8" cy="11.25" r="1" fill="white" />
      </svg>
    );
  }

  if (priority === "none") {
    // Neutral "no priority" glyph — three horizontal dashes.
    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        className={cn("shrink-0", className)}
      >
        {BARS.map((bar) => (
          <rect
            key={bar.x}
            x={bar.x}
            y="7.25"
            width="3"
            height="1.5"
            rx="0.75"
            fill="currentColor"
          />
        ))}
      </svg>
    );
  }

  // low / medium / high — 1, 2 or 3 filled signal bars.
  const filled = FILLED_BARS[priority];
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      className={cn("shrink-0", className)}
    >
      {BARS.map((bar, index) => (
        <rect
          key={bar.x}
          x={bar.x}
          y={bar.y}
          width="3"
          height={bar.height}
          rx="1"
          fill="currentColor"
          opacity={index < filled ? 1 : 0.3}
        />
      ))}
    </svg>
  );
}
